'use client';

import { ChevronLeft, CloseIcon } from '@/components/icons';
import { TSearchProps } from './Search';
import { SelectItem } from './SelectItem';
import * as actions from '@/actions';
import ReactSelect from 'react-select';
import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { TSelectOption } from '@/types';
import { createPortal } from 'react-dom';

const delay = 300;

export const SearchMobile = ({
  activityList,
  selectedActivity,
  handleSelectActivity,
  locationList,
  locationsLoading,
  activitiesLoading,
  selectedLocation,
  handleSelectLocation,
}: TSearchProps) => {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [tab, setTab] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showScrollFade, setShowScrollFade] = useState(false);

  useEffect(() => {
    if (isModalOpen && modalRef.current) {
      setTimeout(() => {
        if (modalRef.current) modalRef.current.classList.add('modal-open');
      }, 0);
    }
  }, [isModalOpen]);

  const updateScrollFade = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const hasOverflow = el.scrollHeight > el.clientHeight + 1;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= 1;
    setShowScrollFade(hasOverflow && !atBottom);
  }, []);

  useEffect(() => {
    if (!isModalOpen) return;
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    updateScrollFade();
    window.addEventListener('resize', updateScrollFade);
    return () => window.removeEventListener('resize', updateScrollFade);
  }, [isModalOpen, tab, locationList, activityList, locationsLoading, activitiesLoading, updateScrollFade]);

  const handleOpen = () => {
    document.body.style.overflow = 'hidden';
    setIsModalOpen(true);
  };

  const handleClose = (event: MouseEvent<HTMLDivElement> | null = null) => {
    if (event) {
      const target = event.target as HTMLElement;
      const isModalContent = target.closest('.filter-modal__content');
      const isCloseButton = target.closest('.filter-modal__close');

      if (!modalRef.current || (!isCloseButton && isModalContent)) {
        return;
      }
    }
    document.body.style.overflow = '';
    modalRef.current?.classList.remove('modal-open');
    setTimeout(() => {
      setIsModalOpen(false);
    }, delay);
  };

  const handleBack = () => {
    setTab(1);
    handleSelectActivity(null);
  };
  const handleSearch = (activity: TSelectOption) => {
    const exp =
      activity && activity.value !== 0 ? activity.slug ?? '' : '';
    const country =
      selectedLocation && selectedLocation.value !== 0 ? selectedLocation.slug ?? '' : '';
    actions.searchMobile({ experience: exp, country });
    handleClose();
  };

  return (
    <>
      {isModalOpen &&
        createPortal(
          <div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-label="Search tours"
            style={{
              paddingTop: 'max(1rem, env(safe-area-inset-top))',
              paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
              paddingLeft: 'max(1rem, env(safe-area-inset-left))',
              paddingRight: 'max(1rem, env(safe-area-inset-right))',
            }}
            className="activity-search-modal fixed w-[100vw] h-[100dvh] inset-0 p-4 bg-gray-900 z-[2000] flex flex-col"
          >
            <div className="flex shrink-0 items-center justify-between px-2.5">
              <div className="p-[10px] pl-0 text-gray-600">
                <div className="flex items-center gap-[2px] text-xl cursor-pointer" onClick={handleBack}>
                  {tab !== 1 && (
                    <>
                      <div className="p-[5px]">
                        <ChevronLeft />
                      </div>
                      <span className="text-md font-semibold">Select location</span>
                    </>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={() => handleClose()}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-800 bg-transparent transition-colors hover:bg-gray-800"
                aria-label="Close search"
              >
                <CloseIcon />
              </button>
            </div>

            <h2 className="shrink-0 px-5">
              {tab === 1 ? 'Where do you want to go' : 'Select Activity'}
            </h2>

            <div className="relative min-h-0 flex-1">
              <div
                ref={scrollRef}
                onScroll={updateScrollFade}
                className="h-full overflow-y-auto overscroll-contain px-2.5 pb-2"
              >
                {tab === 1 && !locationsLoading && (
                  <div className="location-list-mobile flex flex-col gap-2">
                    {locationList.map((location) => (
                      <SelectItem
                        key={location.value}
                        value={location.value}
                        label={location.label}
                        handleClick={() => {
                          setTab(2);
                          handleSelectLocation(location);
                        }}
                      />
                    ))}
                  </div>
                )}

                {tab === 2 && !activitiesLoading && (
                  <div className="activity-list-mobile flex flex-col gap-2">
                    {activityList?.map((activity) => (
                      <SelectItem
                        key={activity.value}
                        value={activity.value}
                        label={activity.label}
                        handleClick={() => {
                          handleSelectActivity(activity);
                          handleSearch(activity);
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>

              <div
                aria-hidden="true"
                className={`pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-gray-900 to-transparent transition-opacity duration-300 ${
                  showScrollFade ? 'opacity-100' : 'opacity-0'
                }`}
              />
            </div>
          </div>,
          document.body
        )}

      <form
        className="relative w-fit max-w-full h-auto flex lg:hidden flex-row items-center text-md text-gray-400 bg-gray-800 py-1 pr-2 pl-8 rounded-full border border-transparent hover:border-gray-700"
        onClick={() => {
          setTab(1);
          handleOpen();
        }}
      >
        <div className="w-[192px] h-12 flex items-center bg-transparent">
          <ReactSelect
            instanceId="location-mobilie"
            name="location"
            placeholder="Anywhere"
            className="select-dropdown no-separator no-arrow text-white bg-gray-800"
            classNamePrefix="select-dropdown"
            classNames={{
              valueContainer: () => 'text-white rounded-lg',
              input: () => 'text-white',
            }}
            value={selectedLocation}
            isDisabled
          />
        </div>

        <div className="h-[20px] border-r border-gray-700"></div>

        <div className="w-[178px] h-12 flex items-center pl-4">
          <ReactSelect
            instanceId="activity-mobile"
            name="activity"
            placeholder="Activity"
            className="select-dropdown no-separator no-arrow text-white bg-gray-800"
            classNamePrefix="select-dropdown"
            classNames={{
              valueContainer: () => 'text-white rounded-lg',
              input: () => 'text-white',
            }}
            value={selectedActivity}
            isDisabled
          />
        </div>
      </form>
    </>
  );
};
